import type { ReactNode } from 'react';
import { Banner } from '@hevoio/hevo-ui-toolkit';
import { isActivateVersionFlipPending } from '../../../core/activate-legacy-teams';
import { useObservableState } from '../../../core/hooks/useObservableState';
import { TeamSettingsService } from '../../../core/services/team-settings.service';
import useService from '../../../injector/hooks/useService';

/**
 * Wraps the `/activation/*` and `/target/*` route elements to say why the version the team was just
 * moved to is not the one on screen.
 *
 * `isLegacyActivateCurrentTeam()` pins its answer for the session, so flipping `h2_activation_enabled`
 * on the backend cannot swap the mounted app under a user mid-session — deliberately, since the two
 * versions address Activations by different identifiers and a live swap would strand any in-flight
 * create/edit. A full load is what adopts the flip, so the user has to be told to do one; without
 * this they would just see the old UI indefinitely with no explanation.
 *
 * Goes away with the rest of the 1.0/2.0 fork at sunset — see `core/activate-legacy-teams.ts`.
 */
export function ActivateVersionFlipNotice({ children }: { children: ReactNode }) {
  const teamSettingsService = useService(TeamSettingsService);

  /*
    Driven off the settings stream rather than checked at render alone: a refetch landing mid-session
    is exactly the event this reports, and nothing else re-renders these routes when it does. The
    subject replays, so a flip that happened before these routes opened still surfaces on mount.
  */
  const [teamSettings] = useObservableState(teamSettingsService.teamSettings$);

  /* No settings means logged out or pre-boot — nothing to reconcile, so say nothing. */
  const isFlipPending = Boolean(teamSettings) && isActivateVersionFlipPending();

  return (
    <>
      {isFlipPending ? (
        <Banner
          open
          color='primary'
          icon='info'
          className='mb-5'
          dataId='activate-version-flip'
          primaryBtnProps={{
            children: 'Reload',
            onClick: () => window.location.reload()
          }}
        >
          {/* Direction-neutral: the same notice covers a rollback off 2.0. */}
          Activate has been switched to a different version for your team. Reload the page to
          continue.
        </Banner>
      ) : null}

      {children}
    </>
  );
}
